import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useProjects, type Project } from "@/hooks/useProjects";

export const Route = createFileRoute("/projects/")({
  component: ProjectsPage,
});

function ProjectsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useProjects();

  const handleCreateProject = () => {
    navigate({ to: "/projects/new" });
  };

  const handleProjectClick = (projectId: string) => {
    navigate({ to: "/projects/$projectId", params: { projectId } });
  };

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={1}>Projects</Title>
            <Button size="md" variant="filled" disabled>
              Create Project
            </Button>
          </Group>

          <Stack gap="md">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={120} radius="md" />
            ))}
          </Stack>
        </Stack>
      </Container>
    );
  }

  const projects = data?.projects || [];

  if (projects.length === 0) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={1}>Projects</Title>
            <Button size="md" variant="filled" onClick={handleCreateProject}>
              Create Project
            </Button>
          </Group>

          <Paper p="xl" withBorder>
            <Stack gap="md" align="center">
              <Text size="lg" c="dimmed">
                No projects yet
              </Text>
              <Text size="sm" c="dimmed">
                Create your first project to get started
              </Text>
              <Button size="md" variant="filled" onClick={handleCreateProject}>
                Create Project
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={1}>Projects</Title>
          <Button size="md" variant="filled" onClick={handleCreateProject}>
            Create Project
          </Button>
        </Group>

        <Stack gap="md">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project.id)}
            />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const getStatusColor = (status: Project["status"]) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "gray";
    }
  };

  const location = [project.city, project.state]
    .filter(Boolean)
    .join(", ") || "No location";

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Title order={3} size="h4">
            {project.name}
          </Title>
          <Badge color={getStatusColor(project.status)} variant="light">
            {project.status}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed">
          {project.address || "No address"}
        </Text>

        <Text size="sm" c="dimmed">
          {location}
        </Text>
      </Stack>
    </Card>
  );
}
